import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { usePostCreateChatMutation } from '@/api/chat';
import { AppRoutes } from '@/constants';
import { selectUserAboutData, useAppSelector } from '@/store';

interface CreateNewChatOptions {
  caseId?: string;
  initialPrompt?: string;
  selectedFileIds?: string[];
  /** Navigate to the chat page after creating the chat. Defaults to true. */
  navigateToChatPage?: boolean;
  /** Where the new chat opens. Defaults to `/chat/:chatId`. */
  chatPath?: (chatId: string) => string;
}

export const useCreateNewChat = () => {
  const navigate = useNavigate();
  const userAboutData = useAppSelector(selectUserAboutData);
  const [triggerPostCreateChat, { isLoading, isSuccess }] = usePostCreateChatMutation();

  const redirectToNewChatPage = useCallback(
    async ({ chatId, options }: { chatId: string; options?: CreateNewChatOptions }) => {
      navigate(options?.chatPath?.(chatId) ?? `/${AppRoutes.Chat}/${chatId}`, {
        replace: true,
        state: {
          caseId: options?.caseId,
          initialPrompt: options?.initialPrompt,
          selectedFileIds: options?.selectedFileIds,
        },
      });
    },
    [navigate],
  );

  const createNewChat = useCallback(
    async (options?: CreateNewChatOptions) => {
      if (!userAboutData?.userId) return;

      const { data } = await triggerPostCreateChat({ userId: userAboutData.userId, matterId: options?.caseId });

      if (data?.chatId && (options?.navigateToChatPage ?? true)) {
        await redirectToNewChatPage({ chatId: data.chatId, options });
      }

      return data?.chatId;
    },
    [userAboutData, triggerPostCreateChat, redirectToNewChatPage],
  );

  return { createNewChat, isLoading, isSuccess };
};
